import { useEffect, useState } from "react";
import { Loader2, Tag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { PageHeader, fmtDate } from "@/components/Bits";

const BLANK = { code: "", kind: "percent", value: "", duration: "once", months: "", applies_to: "both", expires_on: "", max_uses: "", first_time_only: false };
const APPLIES = { membership: "Memberships", mini_plan: "Mini Marketing Plan", both: "Memberships & Mini Plan" };

const Seg = ({ value, options, onChange, testId, nowrap }) => (
  <div className={`flex gap-2 ${nowrap ? "shrink-0" : "flex-wrap"}`}>{options.map(([k, label]) => (
    <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testId}-${k}`}
      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${value === k ? "bg-navy text-white border-navy" : "bg-white border-border hover:border-navy"}`}>{label}</button>
  ))}</div>
);

const describe = (c) => {
  const off = c.percent_off ? `${c.percent_off}% off` : c.amount_off ? `£${c.amount_off} off` : "Discount";
  const dur = { once: "first payment", forever: "every payment", repeating: `${c.months} months` }[c.duration] || "";
  return `${off}${dur ? ` · ${dur}` : ""}${c.applies ? ` · ${APPLIES[c.applies]}` : ""}`;
};

const NewCode = ({ onCreated }) => {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/discount-codes", {
        ...f, code: f.code.trim().toUpperCase(), value: Number(f.value),
        months: f.duration === "repeating" ? Number(f.months) || null : null,
        expires_on: f.expires_on || null, max_uses: Number(f.max_uses) || null,
      });
      toast.success(`${f.code.toUpperCase()} is live. Customers can enter it at checkout.`);
      setF(BLANK); onCreated();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const L = ({ children }) => <div className="text-xs font-mono uppercase text-muted-foreground mb-2">{children}</div>;
  return (
    <form onSubmit={create} className="rounded-2xl border border-border bg-card p-6 lg:p-8 space-y-5 max-w-3xl mb-10" data-testid="code-form">
      <h3 className="text-xl flex items-center gap-2"><Tag className="h-5 w-5 text-pinkb" />Create a code</h3>
      <div className="grid sm:grid-cols-2 gap-5">
        <div><L>Code customers type</L><Input value={f.code} onChange={set("code")} maxLength={20} placeholder="SPRING20" data-testid="code-input" className="bg-white uppercase" /></div>
        <div><L>Discount</L>
          <div className="flex gap-2">
            <Seg value={f.kind} onChange={set("kind")} options={[["percent", "%"], ["amount", "£"]]} testId="code-kind" nowrap />
            <Input type="number" min={1} step="0.01" value={f.value} onChange={set("value")} placeholder={f.kind === "percent" ? "20" : "10"} data-testid="code-value-input" className="bg-white" />
          </div>
        </div>
      </div>
      <div><L>Works on</L><Seg value={f.applies_to} onChange={set("applies_to")} options={Object.entries(APPLIES)} testId="code-applies" /></div>
      <div><L>How long the discount lasts (memberships)</L>
        <div className="flex flex-wrap items-center gap-3">
          <Seg value={f.duration} onChange={set("duration")} options={[["once", "First payment"], ["repeating", "Set months"], ["forever", "Forever"]]} testId="code-duration" />
          {f.duration === "repeating" && <Input type="number" min={1} max={24} value={f.months} onChange={set("months")} placeholder="3" data-testid="code-months-input" className="bg-white w-24" />}
        </div>
        <p className="text-xs text-muted-foreground mt-2">The Mini Marketing Plan is a one-off payment, so it always gets the discount once.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div><L>Expires on (optional)</L><Input type="date" value={f.expires_on} min={new Date().toISOString().slice(0, 10)} onChange={set("expires_on")} data-testid="code-expires-input" className="bg-white" /></div>
        <div><L>Maximum uses (optional)</L><Input type="number" min={1} value={f.max_uses} onChange={set("max_uses")} placeholder="No limit" data-testid="code-max-input" className="bg-white" /></div>
      </div>
      <label className="flex items-center gap-3 text-sm"><Switch checked={f.first_time_only} onCheckedChange={set("first_time_only")} data-testid="code-first-time-switch" />New customers only</label>
      <Button type="submit" disabled={busy || f.code.length < 3 || !f.value} data-testid="code-create-btn" className="rounded-full bg-pinkb hover:bg-pinkb/90 text-white">
        {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create code
      </Button>
    </form>
  );
};

const CodeRow = ({ c, onToggle }) => (
  <div className="flex flex-wrap items-center justify-between gap-4 p-5" data-testid={`code-row-${c.code}`}>
    <div>
      <div className="font-mono font-semibold tracking-wide">{c.code}</div>
      <div className="text-sm text-muted-foreground mt-0.5">{describe(c)}</div>
      <div className="text-xs text-muted-foreground mt-1" data-testid={`code-usage-${c.code}`}>
        Used {c.times_redeemed}{c.max_uses ? ` of ${c.max_uses}` : ""} times{c.expires_at && ` · expires ${fmtDate(c.expires_at)}`}{c.first_time_only && " · new customers only"}
      </div>
    </div>
    <label className="flex items-center gap-2 text-sm">{c.active ? "On" : "Off"}
      <Switch checked={c.active} onCheckedChange={(v) => onToggle(c, v)} data-testid={`code-active-${c.code}`} />
    </label>
  </div>
);

export default function AdminCodes() {
  const [codes, setCodes] = useState(null);
  const load = () => api.get("/admin/discount-codes").then((r) => setCodes(r.data)).catch((e) => { toast.error(errMsg(e)); setCodes([]); });
  useEffect(() => { load(); }, []);
  const toggle = async (c, active) => {
    try {
      await api.post(`/admin/discount-codes/${c.id}/active`, { active });
      setCodes((l) => l.map((x) => (x.id === c.id ? { ...x, active } : x)));
      toast.success(active ? `${c.code} switched on` : `${c.code} switched off. It can't be used at checkout now.`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Offers" title="Discount codes." />
      <p className="text-sm text-muted-foreground max-w-2xl mb-8">Customers type codes in the "Add promotion code" box at checkout. Pro's 2-month minimum still applies. Codes can't be deleted, but switching one off stops it working straight away.</p>
      <NewCode onCreated={load} />
      <div className="rounded-2xl border border-border bg-card divide-y divide-border max-w-3xl" data-testid="codes-list">
        {codes === null && <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
        {codes?.length === 0 && <p className="p-6 text-sm text-muted-foreground">No codes yet.</p>}
        {codes?.map((c) => <CodeRow key={c.id} c={c} onToggle={toggle} />)}
      </div>
    </div>
  );
}
